import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Icon, DatePicker, toast, ExpandedTooltip, SimpleModal } from '@livable/saas-react-components';
import { Alert, Button, FormControl, FormHelperText, Grid, InputAdornment, InputLabel, OutlinedInput, IconButton, Skeleton, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isOldServicePeriod, useOldServicePeriodConfirm } from 'src/components/allocations/OldServicePeriod';
import RangeDatePicker from 'src/components/general/RangeDatePicker';
import { track, events } from 'src/configs/amplitude';
import { useStore } from 'src/stores';
import { refreshCycleBill } from './refreshCycleBill';
import { getExtractedBillDate } from './billDateFromExtraction';
import useModalFooterStyles from 'src/styles/cutomModalFooter';
import { billSchema, baseBillSchema } from 'src/utils/formValidation/Allocations';
import { translate } from 'src/utils/translator';
import { AppfolioPushMessages, useAppfolioPush } from './AppfolioPush';
import { getBillTotalOverride } from './AppfolioPush/billTotalOverride';
import { BILL_AUTOMATION_BANNER, BillAutomationExtractedLineItems, BillAutomationExtractedSummary, getBillAutomationBanner, getBillAutomationErrorList, getBillAutomationWarningList, getOriginalBillLink, getSettledBillBannerCopy, getExtractedInvoiceTotal, getExtractedTotalAmount, canDeleteRecoveredBill, isAllocatedFromBill, getRecoveredBillDeleteNote, getRecoveredBillIntro, narrowExtractionToScope, ProcessCredentialLine } from './BillAutomationExtraction';
import ComparisonTable from './comparisonTable';
import Gwd from './gwd';
import useStyles from './styles';
import { useUploadedBillDelete } from './useUploadedBillDelete';

/** property-service's refusal for an uploaded bill that has been deleted or replaced (LB-5952). */
const SOURCE_BILL_GONE = 'SourceBillGone';

/** The Bill total field before anyone touches it: it mirrors the extraction and sends no override. */
const UNEDITED_BILL_TOTAL = {
  value: '',
  isEdited: false,
  isEditable: false
};

/**
 * The saas-react-components DatePicker does not pass `disabled` through (not in 3.6.0 either), so a
 * locked table cannot disable it. It shows the date in a disabled input instead, the way AllocationForm does.
 */
const LockedDateField = _ref => {
  let id = _ref.id,
    label = _ref.label,
    value = _ref.value,
    width = _ref.width;
  return /*#__PURE__*/React.createElement(FormControl, {
    variant: "outlined",
    fullWidth: !width,
    style: width ? {
      width
    } : undefined
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: id
  }, label), /*#__PURE__*/React.createElement(OutlinedInput, {
    disabled: true,
    endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "start"
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "calendar-picker",
      state: "lightGrey"
    })),
    id: id,
    label: label,
    type: "text",
    value: value ? moment(value).format('MM/DD/YYYY') : ''
  }));
};

/**
 * LB-5835: the create/update bill contract treats an OMITTED field as "leave the stored value
 * alone" and a ZERO as "clear it". An empty input is therefore omitted, UNLESS the field held a
 * real seeded value when the form opened (editing an existing bill with that value already set),
 * in which case emptying it is a real clear and must be sent as 0 rather than left out.
 */
/** The change-guard regex on these amount inputs accepts a comma as a decimal separator; parseFloat only understands a dot. */
const parseAmount = text => parseFloat(String(text).replace(',', '.'));

/** Both empty, or the same calendar day. */
const isSameDay = (a, b) => !a && !b || !!a && !!b && moment(a).isSame(moment(b), 'day');
const buildOmittableAmount = (currentValue, initialValue) => {
  const text = String(currentValue !== null && currentValue !== void 0 ? currentValue : '').trim();
  if (text !== '') return parseAmount(text);
  const hadInitialValue = String(initialValue !== null && initialValue !== void 0 ? initialValue : '').trim() !== '';
  return hadInitialValue ? 0 : undefined;
};
const sentAmount = (currentValue, initialValue) => {
  var _buildOmittableAmount;
  return (_buildOmittableAmount = buildOmittableAmount(currentValue, initialValue)) !== null && _buildOmittableAmount !== void 0 ? _buildOmittableAmount : null;
};
const savedAmount = value => sentAmount(value, value);
const RATE_KEYS = ['consumptionCharges', 'baseChargeAmount', 'totalUsage'];
const opensRecalculateDialog = _ref2 => {
  var _values$date, _values$date2;
  let values = _ref2.values,
    initial = _ref2.initial,
    stored = _ref2.stored;
  const effective = key => {
    var _sentAmount;
    return (_sentAmount = sentAmount(values[key], initial[key])) !== null && _sentAmount !== void 0 ? _sentAmount : savedAmount(stored[key]);
  };
  if (!(effective('consumptionCharges') > 0 && effective('totalUsage') > 0)) return false;
  return RATE_KEYS.some(key => effective(key) !== savedAmount(stored[key])) || ((_values$date = values.date) === null || _values$date === void 0 ? void 0 : _values$date.startDate) !== (stored.startDateOnly || '') || ((_values$date2 = values.date) === null || _values$date2 === void 0 ? void 0 : _values$date2.endDate) !== (stored.endDateOnly || '');
};
const BillDetailsForm = observer(props => {
  var _utility$isEditable, _utility$bill, _store$utilityProvide, _extractedBillData$pr, _utility$bill3, _utility$bill4, _utility$bill$baseCha, _utility$bill5, _utility$bill$consump, _utility$bill6, _utility$bill7, _utility$bill8, _utility$bill$totalUs, _utility$bill9, _utility$bill10, _utility$bill11, _store$allocations$cu2, _property$allocations14, _utility$bill27, _utility$bill28, _utility$bill29, _utility$bill30, _utility$bill31, _utility$bill32, _utility$bill33, _property$allocations16, _property$allocations17, _property$allocations23, _property$allocations24, _property$allocations25, _utility$bill37, _utility$bill38, _utility$bill39, _store$files$fileData, _store$homeDashboardS, _store$homeDashboardS2, _property$allocations26, _property$allocations27, _utility$bill40;
  const classes = useStyles();
  const modalFooterClasses = useModalFooterStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const page = 'Apply Billing';
  const isBillExtractionEnabled = store.account.featureFlagsStore.getIsBillExtractionEnabled;
  /** The AP push UI is gated by the same environment flag the bill-automation service checks. */
  const isApPushEnabled = store.account.featureFlagsStore.getIsPushUtilityBillAppFolioEnabled && store.company.companyFeatureStore.isAppfolio;
  const billInfoAmountsRowMd = isBillExtractionEnabled ? 12 : 6;
  const billAutomationResponse = props.billAutomationResponse,
    handleClose = props.handleClose,
    selectedCycleStartDate = props.selectedCycleStartDate,
    showPreview = props.showPreview,
    _props$utility = props.utility,
    utility = _props$utility === void 0 ? {} : _props$utility,
    isSubmeterUtility = props.isSubmeterUtility,
    onSubmeterBillSaved = props.onSubmeterBillSaved,
    hasSavedMeterRows = props.hasSavedMeterRows,
    _props$hasReadTable = props.hasReadTable,
    hasReadTable = _props$hasReadTable === void 0 ? true : _props$hasReadTable;
  const comparison = utility.comparison || {};
  const currentCompany = store.homeDashboardStore.getCurrentCompany || {};
  const billingTab = currentCompany.billingTab;
  const applyBill = currentCompany.billingTab.applyBill || {};
  const isTableLocked = ((_utility$isEditable = utility.isEditable) !== null && _utility$isEditable !== void 0 ? _utility$isEditable : applyBill.isEditable) === false;
  const billingCycleId = applyBill.billingCycleId;
  const id = ((_utility$bill = utility.bill) === null || _utility$bill === void 0 ? void 0 : _utility$bill.id) || '';
  const property = store.homeDashboardStore.selectedPropertyId === 'all' ? store.homeDashboardStore.getCurrentCompanyProperties.find(p => p.id === applyBill.propertyId) || {} : store.homeDashboardStore.getCurrentProperty || {};
  const propertyId = property.id;
  const uploadedBillDelete = useUploadedBillDelete({
    property
  });
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showBillAutomationDetails = _useState2[0],
    setShowBillAutomationDetails = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isGwdSetting = _useState4[0],
    setIsGwdSetting = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    customChargeOnDate = _useState6[0],
    setCustomChargeOnDate = _useState6[1];
  const _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    invoiceNumber = _useState8[0],
    setInvoiceNumber = _useState8[1];
  /**
   * Shown beside the invoice number, never folded into it. Read-only: nothing consumes it — the AP push
   * identifies a payable by the invoice number and explicitly refuses an account number in that role.
   */
  const _useState9 = useState(''),
    _useState0 = _slicedToArray(_useState9, 2),
    accountNumber = _useState0[0],
    setAccountNumber = _useState0[1];
  const _useState1 = useState(UNEDITED_BILL_TOTAL),
    _useState10 = _slicedToArray(_useState1, 2),
    totalToAllocateAmount = _useState10[0],
    setTotalToAllocateAmount = _useState10[1];
  const billTotalDocumentIdRef = useRef(billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId);
  const _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    postingDate = _useState12[0],
    setPostingDate = _useState12[1];
  const _useState13 = useState(null),
    _useState14 = _slicedToArray(_useState13, 2),
    billDueDate = _useState14[0],
    setBillDueDate = _useState14[1];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _useState15 = useState('Charge on date'),
    _useState16 = _slicedToArray(_useState15, 2),
    datePickerLabel = _useState16[0],
    setDatePickerLabel = _useState16[1];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _useState17 = useState(null),
    _useState18 = _slicedToArray(_useState17, 2),
    defaultChargeOnDateValue = _useState18[0],
    setDefaultChargeOnDateValue = _useState18[1];

  /**
   * A hand-edited Bill total belongs to one document. When the upload response names a different bill —
   * a new file replacing the last one — the edit goes with the old document instead of riding along as
   * the override for the new one.
   */
  useEffect(() => {
    const billId = billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId;
    if (billTotalDocumentIdRef.current === billId) return;
    billTotalDocumentIdRef.current = billId;
    setTotalToAllocateAmount(UNEDITED_BILL_TOTAL);
  }, [billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId]);
  const currentUtilityTypeName = (_store$utilityProvide = store.utilityProviders.utilities.get(applyBill.utilityId)) === null || _store$utilityProvide === void 0 ? void 0 : _store$utilityProvide.utilityTypeName;

  /**
   * Which service on the bill this screen is allocating. The property matters as much as the utility:
   * one bill can charge several properties for the same utility, each with its own subtotal, and the
   * utility alone would read whichever the provider printed first.
   */
  const extractionScope = useMemo(() => ({
    propertyId,
    utilityId: applyBill.utilityId,
    utilityTypeName: currentUtilityTypeName
  }), [propertyId, applyBill.utilityId, currentUtilityTypeName]);
  const setApplyBillAutosave = currentCompany.billingTab.setApplyBillAutosave;
  const appliedExtractedBillIdRef = useRef('');
  // The bill whose read values filled only the bill info fields, over a saved bill (LB-6102), so they fill once.
  const filledBillInfoIdRef = useRef('');
  const lastSyncedUtilityBillIdRef = useRef(undefined);

  // Without the next steps: they name the Bills page's actions (retry, attach), which this screen doesn't
  // have (LB-5918).
  const billAutomationErrorList = useMemo(() => getBillAutomationErrorList(billAutomationResponse, {
    withNextSteps: false
  }), [billAutomationResponse]);
  const billAutomationWarningList = useMemo(() => getBillAutomationWarningList(billAutomationResponse), [billAutomationResponse]);
  const hasBillAutomationErrors = billAutomationErrorList.length > 0;
  const hasBillAutomationWarnings = billAutomationWarningList.length > 0;

  // Bill details are only populated when validation succeeds AND no blocking errors are reported.
  const extractedBillData = (billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.status) === 'Validated' && billAutomationResponse !== null && billAutomationResponse !== void 0 && billAutomationResponse.data && !hasBillAutomationErrors ? billAutomationResponse.data : null;
  /**
   * What the summary and the line items show: this property, this utility. The full payload stays
   * for the AP push and the count below, which are about the whole invoice, not this allocation.
   */
  const scopedBillData = useMemo(() => extractedBillData ? narrowExtractionToScope(extractedBillData, extractionScope) : null, [extractedBillData, extractionScope]);
  const totalExtractedServices = (extractedBillData === null || extractedBillData === void 0 || (_extractedBillData$pr = extractedBillData.properties) === null || _extractedBillData$pr === void 0 ? void 0 : _extractedBillData$pr.reduce((acc, p) => {
    var _p$services;
    return acc + (((_p$services = p.services) === null || _p$services === void 0 ? void 0 : _p$services.length) || 0);
  }, 0)) || 0;
  const additionalUtilitiesCount = Math.max(0, totalExtractedServices - 1);

  /**
   * The document id comes from the bill automation upload response; without it there is nothing to push.
   * Placed after `extractedBillData` because it reads it: the extraction says whether the bill already
   * has an invoice number worth trusting, and the field says what a person put there when it does not.
   */
  const appfolioPush = useAppfolioPush({
    billId: billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId,
    billStatus: billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.status,
    extraction: extractedBillData,
    isEnabled: isApPushEnabled,
    typedInvoiceNumber: invoiceNumber
  });
  useEffect(() => {
    if (!extractedBillData) {
      setShowBillAutomationDetails(false);
    }
  }, [extractedBillData]);
  const getCAD = useCallback(() => {
    if (utility.commonAreaDeduction >= 0) return utility.commonAreaDeduction;
    if (applyBill.commonAreaDeduction >= 0) return applyBill.commonAreaDeduction;
    return 5;
  }, [utility.commonAreaDeduction, applyBill.commonAreaDeduction]);
  const getGoodWillDeduction = useCallback(() => {
    var _utility$bill2;
    if (applyBill.goodWillDeduction !== null && applyBill.goodWillDeduction >= 0) return applyBill.goodWillDeduction;
    if (((_utility$bill2 = utility.bill) === null || _utility$bill2 === void 0 ? void 0 : _utility$bill2.goodwillDeduction) >= 0) return utility.bill.goodwillDeduction;
    return 5;
  }, [applyBill.goodWillDeduction, (_utility$bill3 = utility.bill) === null || _utility$bill3 === void 0 ? void 0 : _utility$bill3.goodwillDeduction]);
  const billInitialValues = {
    amount: ((_utility$bill4 = utility.bill) === null || _utility$bill4 === void 0 ? void 0 : _utility$bill4.amount) || 0,
    // LB-5835: submeter-only inputs, seeded from utility.bill beside amount and goodWillDeduction
    // above. Blank means not entered; `??` rather than `||` because a real stored zero must stay
    // a zero, not collapse into "not entered".
    baseChargeAmount: (_utility$bill$baseCha = (_utility$bill5 = utility.bill) === null || _utility$bill5 === void 0 ? void 0 : _utility$bill5.baseChargeAmount) !== null && _utility$bill$baseCha !== void 0 ? _utility$bill$baseCha : '',
    commonAreaDeduction: getCAD(),
    consumptionCharges: isSubmeterUtility ? (_utility$bill$consump = (_utility$bill6 = utility.bill) === null || _utility$bill6 === void 0 ? void 0 : _utility$bill6.consumptionCharges) !== null && _utility$bill$consump !== void 0 ? _utility$bill$consump : '' : '',
    date: {
      endDate: ((_utility$bill7 = utility.bill) === null || _utility$bill7 === void 0 ? void 0 : _utility$bill7.endDateOnly) || '',
      startDate: ((_utility$bill8 = utility.bill) === null || _utility$bill8 === void 0 ? void 0 : _utility$bill8.startDateOnly) || ''
    },
    goodWillDeduction: getGoodWillDeduction(),
    totalUsage: isSubmeterUtility ? (_utility$bill$totalUs = (_utility$bill9 = utility.bill) === null || _utility$bill9 === void 0 ? void 0 : _utility$bill9.totalUsage) !== null && _utility$bill$totalUs !== void 0 ? _utility$bill$totalUs : '' : ''
  };
  const _useState19 = useState(false),
    _useState20 = _slicedToArray(_useState19, 2),
    isReinitialize = _useState20[0],
    setIsReinitialize = _useState20[1];
  useEffect(() => {
    var _utility$bill0, _utility$bill1;
    if (!isSubmeterUtility) return;
    if ((_utility$bill0 = utility.bill) !== null && _utility$bill0 !== void 0 && _utility$bill0.startDateOnly && (_utility$bill1 = utility.bill) !== null && _utility$bill1 !== void 0 && _utility$bill1.endDateOnly) {
      setIsReinitialize(true);
    }
  }, [(_utility$bill10 = utility.bill) === null || _utility$bill10 === void 0 ? void 0 : _utility$bill10.startDateOnly, (_utility$bill11 = utility.bill) === null || _utility$bill11 === void 0 ? void 0 : _utility$bill11.endDateOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const postBillCreated = async data => {
    const resp = await store.allocations.postBillCreatedAsync.run(data);
    return resp;
  };
  const putBillUpdated = async data => {
    const resp = await store.allocations.putBillUpdatedAsync.run(data);
    return resp;
  };
  const _useState21 = useState(false),
    _useState22 = _slicedToArray(_useState21, 2),
    updateBillChargeOnDateTrigger = _useState22[0],
    setUpdateBillChargeOnDateTrigger = _useState22[1];
  const updateBillChargeOnDate = async (billId, chargeOnDate) => {
    return store.appfolioBillsApi.postBillSettingsAsync.run({
      billId: billId,
      chargeOnDate: chargeOnDate,
      propertyId: propertyId
    }).then(postBillSettingsAsyncResp => {
      if (postBillSettingsAsyncResp !== null && postBillSettingsAsyncResp !== void 0 && postBillSettingsAsyncResp.isSuccessful) {
        if (customChargeOnDate === chargeOnDate) return;
        if (chargeOnDate === null) {
          toast('success', {
            body: [t('Bill charge on date cleared!')]
          });
        } else {
          toast('success', {
            body: [t('Bill charge on date updated!')]
          });
        }
      } else {
        toast('error', {
          body: [t('While updating bill charge on date!')]
        });
      }
    }).finally(() => setUpdateBillChargeOnDateTrigger(prev => !prev));
  };
  const getFileId = () => {
    var _property$allocations, _property$allocations2, _utility$bill12, _utility$bill13, _utility$bill14, _utility$bill15;
    return (_property$allocations = property.allocations) !== null && _property$allocations !== void 0 && (_property$allocations = _property$allocations.tempFileData) !== null && _property$allocations !== void 0 && _property$allocations.fileId ? (_property$allocations2 = property.allocations) === null || _property$allocations2 === void 0 || (_property$allocations2 = _property$allocations2.tempFileData) === null || _property$allocations2 === void 0 ? void 0 : _property$allocations2.fileId : utility !== null && utility !== void 0 && (_utility$bill12 = utility.bill) !== null && _utility$bill12 !== void 0 && _utility$bill12.redactedFileId ? utility === null || utility === void 0 || (_utility$bill13 = utility.bill) === null || _utility$bill13 === void 0 ? void 0 : _utility$bill13.redactedFileId : utility !== null && utility !== void 0 && (_utility$bill14 = utility.bill) !== null && _utility$bill14 !== void 0 && _utility$bill14.fileId ? utility === null || utility === void 0 || (_utility$bill15 = utility.bill) === null || _utility$bill15 === void 0 ? void 0 : _utility$bill15.fileId : '';
  };

  /**
   * The bill-automation bill this allocation is made from, when the allocation does not already say so
   * (LB-5755). Since LB-5898 an upload here goes to bill-automation only, so this is the one record of where
   * the allocation's document is: residents reach it by it, and bill-automation reads it to know the bill is
   * allocated and must not be deleted, edited or retried out from under the allocation.
   *
   * The bill on screen, because it is the one whose document and values the person is allocating. Nothing
   * while a replacement is outstanding (the screen holds no bill then) or once the document was deleted here.
   * And nothing when the allocation already names it, since the attach call answers a request that changes
   * nothing as a failure.
   */
  const getSourceBillIdToLink = () => {
    var _property$allocations3, _utility$bill16, _utility$bill17, _property$allocations4, _utility$bill18;
    const heldBillId = billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId;
    if (!heldBillId || (_property$allocations3 = property.allocations) !== null && _property$allocations3 !== void 0 && (_property$allocations3 = _property$allocations3.tempFileData) !== null && _property$allocations3 !== void 0 && _property$allocations3.isDeleted) return undefined;

    // A bill this screen went and found, rather than one uploaded here now, is only the allocation's own
    // when the allocation has no document of its own. One with a file-service file predates uploads going
    // to bill-automation, and the newest bill for its cycle may be another document altogether.
    const allocationHasOwnFile = !!(utility !== null && utility !== void 0 && (_utility$bill16 = utility.bill) !== null && _utility$bill16 !== void 0 && _utility$bill16.fileId || utility !== null && utility !== void 0 && (_utility$bill17 = utility.bill) !== null && _utility$bill17 !== void 0 && _utility$bill17.redactedFileId);
    if ((_property$allocations4 = property.allocations) !== null && _property$allocations4 !== void 0 && (_property$allocations4 = _property$allocations4.tempFileData) !== null && _property$allocations4 !== void 0 && _property$allocations4.wasRecovered && allocationHasOwnFile) return undefined;
    return heldBillId === (utility === null || utility === void 0 || (_utility$bill18 = utility.bill) === null || _utility$bill18 === void 0 ? void 0 : _utility$bill18.sourceBillId) ? undefined : heldBillId;
  };

  /**
   * @param linkSourceBill false after a create, which carried the link already: the allocation in the store
   *   does not know that yet, so asking again would send the same link twice.
   * @returns whether the allocation bill now carries what it should: true when there was nothing to send.
   */
  const fileAttachedAsync = async function (billIdId) {
    var _property$allocations5;
    let _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref3$linkSourceBill = _ref3.linkSourceBill,
      linkSourceBill = _ref3$linkSourceBill === void 0 ? true : _ref3$linkSourceBill;
    let fileAttachedData = {};
    const sourceBillId = linkSourceBill ? getSourceBillIdToLink() : undefined;
    if ((_property$allocations5 = property.allocations) !== null && _property$allocations5 !== void 0 && (_property$allocations5 = _property$allocations5.tempFileData) !== null && _property$allocations5 !== void 0 && _property$allocations5.isDeleted) {
      fileAttachedData = {
        billId: billIdId,
        fileId: null,
        isShared: false
      };
    } else if (getFileId()) {
      var _property$allocations6, _utility$bill19;
      if ((_property$allocations6 = property.allocations) !== null && _property$allocations6 !== void 0 && (_property$allocations6 = _property$allocations6.tempFileData) !== null && _property$allocations6 !== void 0 && _property$allocations6.isReducted || utility !== null && utility !== void 0 && (_utility$bill19 = utility.bill) !== null && _utility$bill19 !== void 0 && _utility$bill19.redactedFileId) {
        var _utility$bill20, _utility$bill21, _property$allocations7;
        fileAttachedData = {
          billId: billIdId,
          fileId: (_utility$bill20 = utility.bill) !== null && _utility$bill20 !== void 0 && _utility$bill20.fileId ? (_utility$bill21 = utility.bill) === null || _utility$bill21 === void 0 ? void 0 : _utility$bill21.fileId : getFileId() ? getFileId() : '',
          redactedFileId: getFileId(),
          isShared: (_property$allocations7 = property.allocations) === null || _property$allocations7 === void 0 || (_property$allocations7 = _property$allocations7.tempFileData) === null || _property$allocations7 === void 0 ? void 0 : _property$allocations7.isShared
        };
      } else {
        var _property$allocations8;
        fileAttachedData = {
          billId: billIdId,
          fileId: getFileId(),
          isShared: (_property$allocations8 = property.allocations) === null || _property$allocations8 === void 0 || (_property$allocations8 = _property$allocations8.tempFileData) === null || _property$allocations8 === void 0 ? void 0 : _property$allocations8.isShared
        };
      }
      if (sourceBillId) fileAttachedData.sourceBillId = sourceBillId;
    } else if (sourceBillId) {
      // No file-service copy to attach: the document is in bill-automation, and this is how it is found. No
      // isShared either, which on a bill with a source document is its own control's to send.
      fileAttachedData = {
        billId: billIdId,
        sourceBillId
      };
    }
    if (!fileAttachedData.billId) return true;
    const resp = await store.allocations.getFileAttachedAsync.run(fileAttachedData);
    return !!(resp !== null && resp !== void 0 && resp.isSuccessful);
  };

  /**
   * The bill's numbers were saved but its document is not attached, or not linked: without the link, residents
   * never see the document and the screen goes on showing a bill the allocation does not name. So the save is
   * not finished -- the screen stays in edit mode for the person to save again -- and it says so.
   */
  /**
   * The uploaded bill this screen holds was deleted, or retried and replaced, while it was open (LB-5952):
   * property-service refuses to allocate from it, or to link it, and says so in its own message. The screen lets go
   * of it, so saving again does not ask the same and loop. It then finds the cycle's bill as for any table with none
   * -- in a retry, the bill that replaced it -- or the person chooses one.
   * @returns whether that is what the request ran into
   */
  const letGoOfGoneSourceBill = asyncModel => {
    var _asyncModel$error, _property$allocations9, _property$allocations0, _property$allocations1, _property$allocations10;
    if ((asyncModel === null || asyncModel === void 0 || (_asyncModel$error = asyncModel.error) === null || _asyncModel$error === void 0 ? void 0 : _asyncModel$error.errorCode) !== SOURCE_BILL_GONE) return false;
    (_property$allocations9 = property.allocations) === null || _property$allocations9 === void 0 || (_property$allocations0 = _property$allocations9.setTempFileData) === null || _property$allocations0 === void 0 || _property$allocations0.call(_property$allocations9, 'billAutomationResponse', null);
    (_property$allocations1 = property.allocations) === null || _property$allocations1 === void 0 || (_property$allocations10 = _property$allocations1.setTempFileData) === null || _property$allocations10 === void 0 || _property$allocations10.call(_property$allocations1, 'wasRecovered', null);
    return true;
  };
  const stopOnFailedAttach = () => {
    currentCompany.billingTab.setIsModalLoading(false);
    setApplyBillAutosave(false);
    if (letGoOfGoneSourceBill(store.allocations.getFileAttachedAsync)) return;
    toast('error', {
      body: ['The bill was saved, but its document could not be attached to it. Save it again.']
    });
  };

  /** The create body's link: the same bill, sent with the bill so the allocation is never without it. */
  const withSourceBill = data => {
    const sourceBillId = getSourceBillIdToLink();
    return sourceBillId ? _objectSpread(_objectSpread({}, data), {}, {
      sourceBillId
    }) : data;
  };

  /**
   * A saved bill is what the PM made of the bill on screen, read or still being read, so Edit opens over it without
   * filling the read again (BIB-458). Said at the save too, because a PM can save before the read lands, and the read
   * then arrives with nothing on screen to fill.
   */
  const rememberSavedRead = () => {
    var _property$allocations11, _property$allocations12;
    const billId = billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId;
    if (billId) (_property$allocations11 = property.allocations) === null || _property$allocations11 === void 0 || (_property$allocations12 = _property$allocations11.setTempFileData) === null || _property$allocations12 === void 0 || _property$allocations12.call(_property$allocations11, 'appliedReadBillId', billId);
  };
  useEffect(() => {
    const billId = id;
    if (store.company.companyFeatureStore.isAppfolio) {
      if (!!billId && billId !== '') {
        const data = {
          billId,
          allocationCycleMonth: moment(applyBill.cycleName, 'MMM YYYY').month() + 1,
          allocationCycleYear: moment(applyBill.cycleName, 'MMM YYYY').year()
        };
        store.appfolioBillsApi.getSettingsByBillIdV2Async.run(data).then(getSettingsByBillIdAsyncResp => {
          if (getSettingsByBillIdAsyncResp !== null && getSettingsByBillIdAsyncResp !== void 0 && getSettingsByBillIdAsyncResp.isSuccessful) {
            if (getSettingsByBillIdAsyncResp !== null && getSettingsByBillIdAsyncResp !== void 0 && getSettingsByBillIdAsyncResp.response) {
              const _getSettingsByBillIdA = getSettingsByBillIdAsyncResp.response,
                chargeOnDate = _getSettingsByBillIdA.chargeOnDate,
                defaultChargeOnDate = _getSettingsByBillIdA.defaultChargeOnDate;
              setCustomChargeOnDate(chargeOnDate);
              if (defaultChargeOnDate) {
                setDatePickerLabel("Charge on date will be set to ".concat(moment(defaultChargeOnDate).format('MM/DD/YYYY')));
                setDefaultChargeOnDateValue(defaultChargeOnDate);
              }
            }
          }
        });
      } else if (propertyId && applyBill.cycleName) {
        // If billId is not provided, we fetch the default charge on date based on property
        const data = {
          propertyId,
          allocationCycleMonth: moment(applyBill.cycleName, 'MMM YYYY').month() + 1,
          allocationCycleYear: moment(applyBill.cycleName, 'MMM YYYY').year()
        };
        store.appfolioPropertiesApi.getDefaultChargeOnDateAsync.run(data).then(getSettingsByBillIdAsyncResp => {
          if (getSettingsByBillIdAsyncResp !== null && getSettingsByBillIdAsyncResp !== void 0 && getSettingsByBillIdAsyncResp.isSuccessful) {
            setDefaultChargeOnDateValue(getSettingsByBillIdAsyncResp.response);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, updateBillChargeOnDateTrigger]);
  useEffect(() => {
    var _property$allocations13, _utility$bill$id, _utility$bill22, _utility$bill23, _utility$bill24, _utility$bill25, _store$allocations$cu;
    if ((_property$allocations13 = property.allocations) !== null && _property$allocations13 !== void 0 && (_property$allocations13 = _property$allocations13.tempFileData) !== null && _property$allocations13 !== void 0 && _property$allocations13.isDeleted) return;
    const currentBillId = (_utility$bill$id = utility === null || utility === void 0 || (_utility$bill22 = utility.bill) === null || _utility$bill22 === void 0 ? void 0 : _utility$bill22.id) !== null && _utility$bill$id !== void 0 ? _utility$bill$id : '';
    const billIdentityChanged = lastSyncedUtilityBillIdRef.current !== currentBillId;
    if (billIdentityChanged) {
      lastSyncedUtilityBillIdRef.current = currentBillId;
    }

    // After extraction, don't clobber invoice/posting/due when utility is refetched (same bill row)
    if (extractedBillData && !billIdentityChanged) return;
    const serverInvoice = (utility === null || utility === void 0 || (_utility$bill23 = utility.bill) === null || _utility$bill23 === void 0 ? void 0 : _utility$bill23.invoiceNumber) || '';
    const serverPosting = utility !== null && utility !== void 0 && (_utility$bill24 = utility.bill) !== null && _utility$bill24 !== void 0 && _utility$bill24.postingDateOnly ? moment(utility.bill.postingDateOnly).toDate() : null;
    const serverDue = utility !== null && utility !== void 0 && (_utility$bill25 = utility.bill) !== null && _utility$bill25 !== void 0 && _utility$bill25.dueDateOnly ? moment(utility.bill.dueDateOnly).toDate() : null;
    const cycleDue = (_store$allocations$cu = store.allocations.currentCycle) !== null && _store$allocations$cu !== void 0 && _store$allocations$cu.dueDate ? moment(store.allocations.currentCycle.dueDate).toDate() : null;
    if (billIdentityChanged) {
      setInvoiceNumber(serverInvoice);
      // The allocation bill carries no account number, so a new bill clears it rather than inheriting
      // the last one's — an account number belongs to one bill and to nothing else.
      setAccountNumber('');
      setPostingDate(serverPosting);
      setBillDueDate(serverDue || cycleDue || null);
    } else {
      // Same bill after refetch: only overwrite when server sends a value (preserve local edits)
      if (serverInvoice) setInvoiceNumber(serverInvoice);
      if (serverPosting) setPostingDate(serverPosting);
      if (serverDue) setBillDueDate(serverDue);
    }
  }, [(_store$allocations$cu2 = store.allocations.currentCycle) === null || _store$allocations$cu2 === void 0 ? void 0 : _store$allocations$cu2.dueDate, utility, extractedBillData, (_property$allocations14 = property.allocations) === null || _property$allocations14 === void 0 || (_property$allocations14 = _property$allocations14.tempFileData) === null || _property$allocations14 === void 0 ? void 0 : _property$allocations14.isDeleted]);

  /**
   * enableReinitialize alone re-seeds the form from billInitialValues on every change to
   * `utility.bill`, including a store refresh that has nothing to do with what was just typed, which
   * silently empties Consumption charges, Base charge and Bill usage on the way back from Meter
   * details. Formik's own reinitialize is switched off below; this effect is the only reinitialize
   * path for a submeter bill, and only takes it when the form is clean or a submit just succeeded.
   */
  const formikRef = useRef(null);
  const justSubmittedRef = useRef(false);
  const isReinitializeMountedRef = useRef(false);
  /**
   * justSubmittedRef was only ever read/cleared inside the reinitialize effect below, and that
   * effect only runs when one of its seven watched values changes. A submit that re-saves the
   * same numbers changes none of them, so the flag stayed true indefinitely and got consumed by a
   * later, unrelated effect run instead, wiping whatever had been typed by then. submitVersion is
   * bumped on every submit regardless of whether the bill values actually moved, and sits in that
   * effect's dependency array, so the effect is guaranteed to run (and clear the flag) immediately
   * after each submit rather than waiting on an unrelated future change to match.
   */
  const _useState23 = useState(0),
    _useState24 = _slicedToArray(_useState23, 2),
    submitVersion = _useState24[0],
    setSubmitVersion = _useState24[1];

  /**
   * The backend re-prices a submeter cycle's saved rows when the bill's consumption
   * charges, base charge, total usage or service dates change and the bill carries both consumption charges
   * and usage above 0, or when a bill is added to such a cycle. Confirm first; Cancel must cancel the
   * bill update entirely, not save the bill and skip the re-price (that split state -- new bill values,
   * stale meter prices -- is the defect this confirms against). skipRecalculateConfirmRef lets the
   * confirmed resubmit fall through the same onSubmit without looping back into its own dialog.
   */
  const _useState25 = useState(false),
    _useState26 = _slicedToArray(_useState25, 2),
    pendingRecalculateConfirm = _useState26[0],
    setPendingRecalculateConfirm = _useState26[1];
  const skipRecalculateConfirmRef = useRef(false);

  /**
   * LB-6030: a bill whose service period ended more than three months ago is calculated only after a confirmation,
   * which the allocation records. Asked for a new allocation, or for an edit that moves the period; an amount-only edit
   * keeps the answer already given. `confirmedOldPeriodRef` lets the confirmed resubmit through, as the ref above does.
   */
  const oldServicePeriod = useOldServicePeriodConfirm();
  const confirmedOldPeriodRef = useRef(false);
  const formik = useFormik({
    initialValues: billInitialValues,
    enableReinitialize: false,
    onSubmit: async values => {
      var _values$date3, _values$date4, _formik$initialValues, _formik$initialValues2;
      if (isTableLocked) {
        skipRecalculateConfirmRef.current = false;
        confirmedOldPeriodRef.current = false;
        return;
      }
      if (isSubmeterUtility && hasSavedMeterRows && !skipRecalculateConfirmRef.current) {
        var _utility$bill26;
        const stored = id ? (_utility$bill26 = utility.bill) !== null && _utility$bill26 !== void 0 ? _utility$bill26 : {} : {};
        if (opensRecalculateDialog({
          values,
          initial: formik.initialValues,
          stored
        })) {
          setPendingRecalculateConfirm(true);
          return;
        }
      }
      const period = {
        endDateOnly: (_values$date3 = values.date) === null || _values$date3 === void 0 ? void 0 : _values$date3.endDate,
        startDateOnly: (_values$date4 = values.date) === null || _values$date4 === void 0 ? void 0 : _values$date4.startDate
      };
      const isPeriodChanged = !id || !isSameDay(period.startDateOnly, (_formik$initialValues = formik.initialValues.date) === null || _formik$initialValues === void 0 ? void 0 : _formik$initialValues.startDate) || !isSameDay(period.endDateOnly, (_formik$initialValues2 = formik.initialValues.date) === null || _formik$initialValues2 === void 0 ? void 0 : _formik$initialValues2.endDate);
      if (!confirmedOldPeriodRef.current && isPeriodChanged && isOldServicePeriod(period.endDateOnly)) {
        oldServicePeriod.confirmIfOld([period], () => {
          confirmedOldPeriodRef.current = true;
          formik.submitForm();
        }, {
          confirmLabel: t('Calculate allocation'),
          // Asked after the recalculation dialog, if that came first: cancelling here undoes its yes as well.
          onCancel: () => {
            skipRecalculateConfirmRef.current = false;
          },
          title: t('Allocate a bill for a long-past period?')
        });
        return;
      }
      const confirmedOldServicePeriod = confirmedOldPeriodRef.current;
      confirmedOldPeriodRef.current = false;
      skipRecalculateConfirmRef.current = false;
      track(events.clicked('Calculate Allocation'), {
        page,
        isSubmeterUtility: !!isSubmeterUtility,
        isExtracted: !!extractedBillData,
        utility: applyBill.utilityName
      });
      currentCompany.billingTab.setIsModalLoading(true);
      setApplyBillAutosave(true);
      const data = {
        amount: parseFloat(values.amount),
        endDateOnly: moment(values.date.endDate).format(moment.HTML5_FMT.DATE),
        goodWillDeduction: parseFloat(values.goodWillDeduction),
        isExcluded: true,
        startDateOnly: moment(values.date.startDate).format(moment.HTML5_FMT.DATE)
      };
      if (confirmedOldServicePeriod) data.confirmedOldServicePeriod = true;
      // The printed date teaches the utility when its bills are ready (LB-6032). Left out when there is no extraction
      // or it falls outside the window, so the stored date of a bill typed by hand is never cleared.
      const billDateOnly = getExtractedBillDate(extractedBillData, data.startDateOnly, data.endDateOnly);
      if (billDateOnly) data.billDateOnly = billDateOnly;
      if (isSubmeterUtility) {
        const consumptionCharges = buildOmittableAmount(values.consumptionCharges, formik.initialValues.consumptionCharges);
        if (consumptionCharges !== undefined) data.consumptionCharges = consumptionCharges;
        const baseChargeAmount = buildOmittableAmount(values.baseChargeAmount, formik.initialValues.baseChargeAmount);
        if (baseChargeAmount !== undefined) data.baseChargeAmount = baseChargeAmount;
        const totalUsage = buildOmittableAmount(values.totalUsage, formik.initialValues.totalUsage);
        if (totalUsage !== undefined) data.totalUsage = totalUsage;
        const billPayload = id ? putBillUpdated(_objectSpread(_objectSpread({}, data), {}, {
          id
        })) : postBillCreated(withSourceBill(_objectSpread(_objectSpread({}, data), {}, {
          billingCycleId,
          propertyId,
          utilityId: applyBill.utilityId
        })));
        billPayload.then(resp => {
          if (!resp || !(resp !== null && resp !== void 0 && resp.isSuccessful)) {
            if (!id) letGoOfGoneSourceBill(store.allocations.postBillCreatedAsync);
            currentCompany.billingTab.setIsModalLoading(false);
            return;
          }
          const billId = id || (resp === null || resp === void 0 ? void 0 : resp.response);
          let isAttached = true;
          return Promise.all([fileAttachedAsync(billId, {
            linkSourceBill: !!id
          }).then(attached => {
            isAttached = attached;
          }), updateBillChargeOnDate(billId, customChargeOnDate)]).then(() => refreshCycleBill({
            store,
            billingTab,
            allocations: property.allocations,
            propertyId,
            billingCycleId,
            utilityId: applyBill.utilityId,
            isSubmeterUtility
          })).then(() => {
            if (!isAttached) {
              stopOnFailedAttach();
              return;
            }
            currentCompany.billingTab.setIsModalLoading(false);
            // Lets the reinitialize effect below re-seed from the re-priced bill even
            // though the form is still dirty from what was just typed and submitted.
            justSubmittedRef.current = true;
            setSubmitVersion(v => v + 1);
            onSubmeterBillSaved === null || onSubmeterBillSaved === void 0 || onSubmeterBillSaved();
          });
        });
      } else if (id) {
        putBillUpdated(_objectSpread(_objectSpread({}, data), {}, {
          id
        })).then(resp => {
          if (!resp || !(resp !== null && resp !== void 0 && resp.isSuccessful)) {
            currentCompany.billingTab.setIsModalLoading(false);
            return;
          }
          if (resp !== null && resp !== void 0 && resp.isSuccessful) {
            // In turn, not together: read alongside the link, the table can come back still naming the old
            // bill, and the screen would then offer to delete the one just allocated.
            return fileAttachedAsync(id).then(isAttached => refreshCycleBill({
              store,
              billingTab,
              allocations: property.allocations,
              propertyId,
              billingCycleId,
              utilityId: applyBill.utilityId,
              isSubmeterUtility
            }).then(() => {
              if (!isAttached) {
                stopOnFailedAttach();
                return;
              }
              rememberSavedRead();
              setApplyBillAutosave(true);
              setTimeout(() => setApplyBillAutosave(false), 2000);
              currentCompany.billingTab.setIsBillEdit(false);
            }));
          }
        });
      } else {
        postBillCreated(withSourceBill(_objectSpread(_objectSpread({}, data), {}, {
          billingCycleId,
          propertyId,
          utilityId: applyBill.utilityId
        }))).then(resp => {
          if (!resp || !(resp !== null && resp !== void 0 && resp.isSuccessful)) {
            letGoOfGoneSourceBill(store.allocations.postBillCreatedAsync);
            currentCompany.billingTab.setIsModalLoading(false);
            return;
          }
          if (resp !== null && resp !== void 0 && resp.isSuccessful) {
            return Promise.all([fileAttachedAsync(resp === null || resp === void 0 ? void 0 : resp.response, {
              linkSourceBill: false
            }), updateBillChargeOnDate(resp === null || resp === void 0 ? void 0 : resp.response, customChargeOnDate), refreshCycleBill({
              store,
              billingTab,
              allocations: property.allocations,
              propertyId,
              billingCycleId,
              utilityId: applyBill.utilityId,
              isSubmeterUtility
            })]).then(_ref4 => {
              let _ref5 = _slicedToArray(_ref4, 1),
                isAttached = _ref5[0];
              if (!isAttached) {
                stopOnFailedAttach();
                return;
              }
              rememberSavedRead();
              setApplyBillAutosave(true);
              setTimeout(() => setApplyBillAutosave(false), 2000);
              currentCompany.billingTab.setIsBillEdit(false);
            });
          }
        });
      }
    },
    validationSchema: isSubmeterUtility ? billSchema : baseBillSchema
  });
  formikRef.current = formik;
  const handleCancelRecalculate = () => {
    track(events.clicked('Cancel Recalculate Meter Amounts'), {
      page,
      utility: applyBill.utilityName
    });
    setPendingRecalculateConfirm(false);
  };
  const handleConfirmRecalculate = () => {
    track(events.clicked('Confirm Recalculate Meter Amounts'), {
      page,
      utility: applyBill.utilityName
    });
    skipRecalculateConfirmRef.current = true;
    setPendingRecalculateConfirm(false);
    formik.submitForm();
  };

  /**
   * The only reinitialize path for a submeter bill (Formik's own enableReinitialize is off,
   * above). Fires when a value the bill inputs are seeded from actually changes on the server,
   * including the re-price a bill edit now triggers on the saved meter rows, and only applies it
   * when the form is clean or `justSubmittedRef` says this change is the result of the save the
   * person just made. Either way a dirty, not-just-submitted form is left alone: what was typed wins.
   * `submitVersion` in the dependency list is what guarantees this runs right after every submit,
   * not just the ones that happen to change a watched bill value (see the comment by its `useState`).
   */
  useEffect(() => {
    var _formikRef$current;
    if (!isSubmeterUtility) return;
    if (!isReinitializeMountedRef.current) {
      isReinitializeMountedRef.current = true;
      return;
    }
    const shouldReinitialize = justSubmittedRef.current || !((_formikRef$current = formikRef.current) !== null && _formikRef$current !== void 0 && _formikRef$current.dirty);
    justSubmittedRef.current = false;
    if (shouldReinitialize) {
      var _formikRef$current2;
      (_formikRef$current2 = formikRef.current) === null || _formikRef$current2 === void 0 || _formikRef$current2.resetForm({
        values: billInitialValues
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmeterUtility, submitVersion, (_utility$bill27 = utility.bill) === null || _utility$bill27 === void 0 ? void 0 : _utility$bill27.amount, (_utility$bill28 = utility.bill) === null || _utility$bill28 === void 0 ? void 0 : _utility$bill28.baseChargeAmount, (_utility$bill29 = utility.bill) === null || _utility$bill29 === void 0 ? void 0 : _utility$bill29.consumptionCharges, (_utility$bill30 = utility.bill) === null || _utility$bill30 === void 0 ? void 0 : _utility$bill30.totalUsage, (_utility$bill31 = utility.bill) === null || _utility$bill31 === void 0 ? void 0 : _utility$bill31.startDateOnly, (_utility$bill32 = utility.bill) === null || _utility$bill32 === void 0 ? void 0 : _utility$bill32.endDateOnly, (_utility$bill33 = utility.bill) === null || _utility$bill33 === void 0 ? void 0 : _utility$bill33.goodwillDeduction, utility.commonAreaDeduction, applyBill.commonAreaDeduction, applyBill.goodWillDeduction]);

  /**
   * Puts the bill fields back to empty, along with everything derived from the extraction that filled
   * them. Shared, so removing a bill leaves the same state however it was removed.
   */
  const clearExtractedBillFields = useCallback(() => {
    setShowBillAutomationDetails(false);
    setIsGwdSetting(false);
    setInvoiceNumber('');
    setAccountNumber('');
    setPostingDate(null);
    setBillDueDate(null);
    setCustomChargeOnDate(null);
    setDatePickerLabel('Charge on date');
    setDefaultChargeOnDateValue(null);
    setTotalToAllocateAmount(UNEDITED_BILL_TOTAL);
    appliedExtractedBillIdRef.current = '';
    filledBillInfoIdRef.current = '';
    const f = formikRef.current;
    if (f) {
      f.resetForm({
        values: {
          amount: 0,
          baseChargeAmount: '',
          commonAreaDeduction: getCAD(),
          consumptionCharges: '',
          date: {
            endDate: '',
            startDate: ''
          },
          goodWillDeduction: getGoodWillDeduction(),
          totalUsage: ''
        }
      });
    }
    setIsReinitialize(true);
  }, [getCAD, getGoodWillDeduction]);
  useEffect(() => {
    var _property$allocations15;
    if (!((_property$allocations15 = property.allocations) !== null && _property$allocations15 !== void 0 && (_property$allocations15 = _property$allocations15.tempFileData) !== null && _property$allocations15 !== void 0 && _property$allocations15.isDeleted)) return;
    clearExtractedBillFields();
  }, [(_property$allocations16 = property.allocations) === null || _property$allocations16 === void 0 || (_property$allocations16 = _property$allocations16.tempFileData) === null || _property$allocations16 === void 0 ? void 0 : _property$allocations16.isDeleted, clearExtractedBillFields]);

  /**
   * The bill these fields were filled from is gone — deleted, or replaced by a newly attached file.
   *
   * Only when an extraction had actually been applied, which the ref records: a person who typed the
   * fields in themselves has nothing here to clear, and clearing anyway would throw away their work.
   * A deleted bill takes its allocation with it (LB-5952), and the allocation reloaded empty clears the rest.
   */
  useEffect(() => {
    if (billAutomationResponse !== null && billAutomationResponse !== void 0 && billAutomationResponse.billId) return;
    if (!appliedExtractedBillIdRef.current) return;
    clearExtractedBillFields();
  }, [billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId, clearExtractedBillFields]);

  // Another bill on screen, or one on its way up to replace it: the saved bill shown beside the one brought back is no
  // longer what the screen holds, whether or not the new one exists or has been read yet. Before the effect below,
  // which reads it.
  const hasUnfinishedReplacement = !!((_property$allocations17 = property.allocations) !== null && _property$allocations17 !== void 0 && (_property$allocations17 = _property$allocations17.tempFileData) !== null && _property$allocations17 !== void 0 && _property$allocations17.hasUnfinishedReplacement);
  useEffect(() => {
    if (!filledBillInfoIdRef.current) return;
    const billId = billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId;
    if (hasUnfinishedReplacement || billId && billId !== filledBillInfoIdRef.current) {
      filledBillInfoIdRef.current = '';
    }
  }, [billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId, hasUnfinishedReplacement]);

  /**
   * The saved bill shown beside a bill brought back for it is gone: deleting the bill it was made from archives the
   * allocation with it (LB-5952). Nothing of that allocation stays on screen, or the next save would make a new bill
   * from it. Keyed on the allocation rather than the extraction, because its read values never filled these fields.
   */
  useEffect(() => {
    if (id || !filledBillInfoIdRef.current) return;
    clearExtractedBillFields();
  }, [id, clearExtractedBillFields]);
  useEffect(() => {
    var _property$allocations18, _property$allocations19, _property$allocations20, _utility$bill34, _utility$bill35, _utility$bill36, _property$allocations21, _property$allocations22;
    if ((_property$allocations18 = property.allocations) !== null && _property$allocations18 !== void 0 && (_property$allocations18 = _property$allocations18.tempFileData) !== null && _property$allocations18 !== void 0 && _property$allocations18.isDeleted) return;
    const billId = billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId;
    // Only apply when extraction succeeded without blocking errors.
    if (!extractedBillData || !billId) return;
    if (appliedExtractedBillIdRef.current === billId) return;
    // A bill brought back for a cycle that already has a saved bill is the document that bill was made from, and the
    // saved bill holds what the PM corrected on it, among them its service dates and amount. Filling those from the
    // read values would have the next save undo the corrections (LB-6102). The bill info fields are not saved on the
    // allocation bill, so the read values are the only ones there are, and they still fill.
    // A file uploaded here is never recovered, so it fills everything, once.
    const wasRecovered = !!((_property$allocations19 = property.allocations) !== null && _property$allocations19 !== void 0 && (_property$allocations19 = _property$allocations19.tempFileData) !== null && _property$allocations19 !== void 0 && _property$allocations19.wasRecovered);
    // Brought back before the table has said whether the cycle has a saved bill: wait for it, or a first open would
    // read "no saved bill" and fill everything, and nothing would put the saved values back once it arrived. The
    // screen's loader covers the form while that read is out (content.jsx), so nothing is typed meanwhile.
    if (wasRecovered && !id && !hasReadTable) return;
    // Opened again over a saved bill, for a read this screen already filled the form from: Edit after a save (BIB-458).
    // Edit mounts the form afresh, so only the screen remembers the read was applied. What was saved since is what the
    // PM made of it, and filling the read over it again had every save undo the corrections before it. Not the saved
    // bill's source: an upload links that the moment it lands, long before its read fills anything.
    const isReopenedAfterFill = !wasRecovered && !!id && ((_property$allocations20 = property.allocations) === null || _property$allocations20 === void 0 || (_property$allocations20 = _property$allocations20.tempFileData) === null || _property$allocations20 === void 0 ? void 0 : _property$allocations20.appliedReadBillId) === billId;
    const isOverSavedBill = wasRecovered && !!id || isReopenedAfterFill;
    if (isOverSavedBill && filledBillInfoIdRef.current === billId) return;
    // Only from the saved bill's own document: the one it names, or, naming none, the cycle's bill when the saved bill
    // has no file of its own. Any other is about to be let go of (allocationBillLookup), and its read values are not
    // this bill's to show.
    const savedBillHasOwnFile = !!((_utility$bill34 = utility.bill) !== null && _utility$bill34 !== void 0 && _utility$bill34.fileId || (_utility$bill35 = utility.bill) !== null && _utility$bill35 !== void 0 && _utility$bill35.redactedFileId);
    const isSavedBillsDocument = (_utility$bill36 = utility.bill) !== null && _utility$bill36 !== void 0 && _utility$bill36.sourceBillId ? utility.bill.sourceBillId === billId : !savedBillHasOwnFile;
    if (isOverSavedBill && !isSavedBillsDocument) return;
    const data = extractedBillData;
    const getVal = keys => {
      const key = keys.find(k => data[k] !== undefined && data[k] !== null);
      return key ? data[key] : null;
    };
    const getDate = keys => {
      const v = getVal(keys);
      if (!v) return null;
      const m = moment(v);
      return m.isValid() ? m : null;
    };

    // Invoice keys only. `accountNumber` used to lead this list, so it won whenever extraction found one
    // — displacing a real invoice number that sat right beside it, and showing as "Invoice number" the
    // one value the AP push explicitly refuses in that role (it repeats on every statement a biller
    // sends). The two are separate fields now, and neither ever fills from the other.
    const extractedInvoiceNumber = getVal(['invoiceNumber', 'invoiceNo', 'invoice']);
    const extractedAccountNumber = getVal(['accountNumber']);
    const extractedPostingDate = getDate(['billDate', 'postingDate', 'postingDateOnly']);
    const extractedDueDate = getDate(['dueDate', 'dueDateOnly', 'billDueDate']);
    const extractedStartDate = getDate(['serviceStartDate', 'serviceStartDateOnly', 'startDateOnly']);
    const extractedEndDate = getDate(['serviceEndDate', 'serviceEndDateOnly', 'endDateOnly']);
    const extractedAmount = getExtractedTotalAmount(data, extractionScope);
    if (extractedInvoiceNumber != null) setInvoiceNumber(String(extractedInvoiceNumber));
    if (extractedAccountNumber != null) setAccountNumber(String(extractedAccountNumber));
    if (extractedPostingDate) setPostingDate(extractedPostingDate.toDate());
    if (extractedDueDate) setBillDueDate(extractedDueDate.toDate());

    // Not recorded as applied: the bill going away later must not clear what is the saved bill's own.
    if (isOverSavedBill) {
      filledBillInfoIdRef.current = billId;
      return;
    }
    const formikInstance = formikRef.current;
    if (formikInstance) {
      if (extractedStartDate || extractedEndDate) {
        var _formikInstance$value, _formikInstance$value2;
        formikInstance.setFieldValue('date', {
          startDate: extractedStartDate ? extractedStartDate.format(moment.HTML5_FMT.DATE) : ((_formikInstance$value = formikInstance.values.date) === null || _formikInstance$value === void 0 ? void 0 : _formikInstance$value.startDate) || '',
          endDate: extractedEndDate ? extractedEndDate.format(moment.HTML5_FMT.DATE) : ((_formikInstance$value2 = formikInstance.values.date) === null || _formikInstance$value2 === void 0 ? void 0 : _formikInstance$value2.endDate) || ''
        });
        // RangeDatePicker keeps internal state; it only syncs from props when reinitialize is true
        setIsReinitialize(true);
      }
      if (extractedAmount !== null) {
        formikInstance.setFieldValue('amount', extractedAmount);
      }
    }
    appliedExtractedBillIdRef.current = billId;
    // Kept by the screen, which outlives this form, so the form opened again by Edit fills from it only once (BIB-458).
    (_property$allocations21 = property.allocations) === null || _property$allocations21 === void 0 || (_property$allocations22 = _property$allocations21.setTempFileData) === null || _property$allocations22 === void 0 || _property$allocations22.call(_property$allocations21, 'appliedReadBillId', billId);
    // What is on screen is this document's now, not a saved bill's shown beside a recovered one.
    filledBillInfoIdRef.current = '';
  }, [extractionScope, billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId, extractedBillData, property.allocations, (_property$allocations23 = property.allocations) === null || _property$allocations23 === void 0 || (_property$allocations23 = _property$allocations23.tempFileData) === null || _property$allocations23 === void 0 ? void 0 : _property$allocations23.appliedReadBillId, (_property$allocations24 = property.allocations) === null || _property$allocations24 === void 0 || (_property$allocations24 = _property$allocations24.tempFileData) === null || _property$allocations24 === void 0 ? void 0 : _property$allocations24.isDeleted, (_property$allocations25 = property.allocations) === null || _property$allocations25 === void 0 || (_property$allocations25 = _property$allocations25.tempFileData) === null || _property$allocations25 === void 0 ? void 0 : _property$allocations25.wasRecovered, id, hasReadTable, (_utility$bill37 = utility.bill) === null || _utility$bill37 === void 0 ? void 0 : _utility$bill37.sourceBillId, (_utility$bill38 = utility.bill) === null || _utility$bill38 === void 0 ? void 0 : _utility$bill38.fileId, (_utility$bill39 = utility.bill) === null || _utility$bill39 === void 0 ? void 0 : _utility$bill39.redactedFileId]);
  const isCycleFromPreviousMonth = () => {
    if (!formik.values.date.startDate) {
      const today = moment().toDate();
      const cycleStartDate = moment(selectedCycleStartDate).toDate();
      return moment(cycleStartDate).isBefore(today, 'month');
    }
  };
  const getComparisonBlock = () => {
    if (showPreview) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: classes.allocationTotal
      }, "Allocation total: ", /*#__PURE__*/React.createElement("b", null, "$", utility.allocated.toFixed(2)), comparison !== null && comparison !== void 0 && comparison.isBillIncreasedBy20Percent ? /*#__PURE__*/React.createElement(Icon, {
        state: "warning",
        type: "arrow-down"
      }) : null), /*#__PURE__*/React.createElement("div", {
        className: classes.sectionTitle
      }, "Allocation history"), comparison !== null && comparison !== void 0 && comparison.isBillIncreasedBy20Percent ? /*#__PURE__*/React.createElement("div", {
        className: classes.allocationWarning
      }, /*#__PURE__*/React.createElement("div", {
        className: classes.allocationWarningContent
      }, /*#__PURE__*/React.createElement(Icon, {
        type: "warning",
        state: "warning"
      }), /*#__PURE__*/React.createElement("span", null, "Your allocation amount has increased by more than 20% since last time. "))) : null, /*#__PURE__*/React.createElement("div", {
        className: classes.allocationTotalTable
      }, /*#__PURE__*/React.createElement("div", {
        className: classes.allocationTotalTableContent
      }, /*#__PURE__*/React.createElement(ComparisonTable, {
        comparison: comparison
      }))));
    } else if (applyBill.status === 'PendingCompletion') {
      const comparisonWithoutUtility = property === null || property === void 0 ? void 0 : property.allocations.comparisonById.get(billingCycleId + applyBill.utilityId);
      if ((comparisonWithoutUtility === null || comparisonWithoutUtility === void 0 ? void 0 : comparisonWithoutUtility.allocations.length) === 0) return null;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: classes.allocationTotal
      }), /*#__PURE__*/React.createElement("div", {
        className: classes.sectionTitle
      }, "Allocation history"), /*#__PURE__*/React.createElement("div", {
        className: classes.allocationTotalTable
      }, /*#__PURE__*/React.createElement("div", {
        className: classes.allocationTotalTableContent
      }, /*#__PURE__*/React.createElement(ComparisonTable, {
        comparison: comparisonWithoutUtility
      }))));
    }
  };
  const getLabel = () => {
    if (customChargeOnDate) return 'Charge on date';else if (defaultChargeOnDateValue) return moment(defaultChargeOnDateValue).format('MM/DD/YYYY');
    return 'Charge on date';
  };
  const handleSkipBill = () => {
    var _property$buildingInf, _property$dashboard;
    track(events.clicked('Skip Bill'), {
      page,
      utility: applyBill.utilityName
    });
    billingTab.setSkipModal({
      allocationCycleId: applyBill.allocationCycleId,
      billingCycleId,
      buildingAddress: applyBill.buildingAddress || ((_property$buildingInf = property.buildingInformation) === null || _property$buildingInf === void 0 ? void 0 : _property$buildingInf.buildingAddress) || ((_property$dashboard = property.dashboard) === null || _property$dashboard === void 0 ? void 0 : _property$dashboard.propertyAddress),
      cycleName: applyBill.cycleName,
      isEdit: false,
      propertyId,
      utilityId: applyBill.utilityId,
      utilityName: applyBill.utilityName
    });
    billingTab.setSkipModalOpen(true);
    handleClose();
  };

  /**
   * What the provider is owed for the whole bill file, which is not the same as what is being
   * allocated: the amount above is narrowed to the one utility this table charges for. Read from
   * the extraction so a bill covering several utilities shows the full invoice here, not the slice.
   *
   * A manual edit still wins, and with nothing extracted it mirrors the amount as it used to, so a
   * bill whose extraction carries no `invoiceTotals` behaves exactly as before.
   */
  const extractedInvoiceTotal = useMemo(() => getExtractedInvoiceTotal(extractedBillData), [extractedBillData]);
  const totalToAllocateAmountValue = totalToAllocateAmount.isEdited ? totalToAllocateAmount.value : extractedInvoiceTotal !== null && extractedInvoiceTotal !== void 0 ? extractedInvoiceTotal : formik.values.amount;
  const handleEditTotalToAllocateAmount = () => {
    track(events.clicked('Edit Total To Allocate Amount'), {
      page,
      utility: applyBill.utilityName
    });
    setTotalToAllocateAmount(prev => _objectSpread(_objectSpread({}, prev), {}, {
      value: totalToAllocateAmountValue,
      isEditable: true
    }));
  };
  const handleSaveTotalToAllocateAmount = () => {
    track(events.clicked('Save Total To Allocate Amount'), {
      page,
      utility: applyBill.utilityName
    });
    setTotalToAllocateAmount(prev => _objectSpread(_objectSpread({}, prev), {}, {
      isEditable: false
    }));
  };

  /**
   * Warns, never blocks, when Consumption charges plus Base charge does not
   * reconcile against Amount to allocate (this bill's own dollar figure — not `totalToAllocateAmount`
   * / "Bill total", the AP-Push/extraction field for a whole multi-utility invoice file, which is
   * blank whenever isApPushEnabled is off and so cannot be what a universal QA check reconciles
   * against). Silent until the PM has actually typed one of the two amounts, so a legacy submeter
   * bill saved before these fields existed does not open to an unearned warning.
   */
  const billReconciliationWarning = useMemo(() => {
    var _formik$values$consum, _formik$values$baseCh;
    if (!isSubmeterUtility) return false;
    const consumptionText = String((_formik$values$consum = formik.values.consumptionCharges) !== null && _formik$values$consum !== void 0 ? _formik$values$consum : '').trim();
    const baseChargeText = String((_formik$values$baseCh = formik.values.baseChargeAmount) !== null && _formik$values$baseCh !== void 0 ? _formik$values$baseCh : '').trim();
    if (consumptionText === '' && baseChargeText === '') return false;
    const amountValue = parseAmount(formik.values.amount);
    if (!Number.isFinite(amountValue)) return false;
    const consumptionValue = parseAmount(consumptionText) || 0;
    const baseChargeValue = parseAmount(baseChargeText) || 0;
    // Compare in cents so float noise (0.1 + 0.2 !== 0.3) can't fire a false warning.
    return Math.round((consumptionValue + baseChargeValue) * 100) !== Math.round(amountValue * 100);
  }, [isSubmeterUtility, formik.values.consumptionCharges, formik.values.baseChargeAmount, formik.values.amount]);

  /**
   * A hand-edited Bill total overrides what the push sends to AppFolio — it used to be display-only, with
   * the Edit control saving nowhere. Only a SAVED edit is sent (the button is disabled while one is
   * open); an untouched or cleared field sends nothing, and the push reads what OCR extracted for itself.
   */
  const handlePushApToAppFolio = () => {
    track(events.clicked('Push AP to AppFolio'), {
      page,
      utility: applyBill.utilityName
    });
    appfolioPush.handlePush(getBillTotalOverride(totalToAllocateAmount));
  };
  const extractedBillFileId = getFileId();
  // A recovered bill was never uploaded through the file service in this session, so there is no
  // fileDataById entry to read a name from — the bill carries its own, and it is the only one there is.
  const extractedBillFileName = extractedBillFileId ? ((_store$files$fileData = store.files.fileDataById.get(extractedBillFileId)) === null || _store$files$fileData === void 0 ? void 0 : _store$files$fileData.fileName) || '' : (billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.fileName) || '';
  const billAutomationCustomerName = ((_store$homeDashboardS = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.companyLegalName) || ((_store$homeDashboardS2 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS2 === void 0 ? void 0 : _store$homeDashboardS2.name) || '';
  const automationStatus = billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.status;
  /**
   * Whether there is a bill behind this screen at all — the gate on every extraction banner below.
   *
   * A file-service id is no longer the only way to have one. A bill recovered for this cycle was
   * uploaded in an earlier session and its document lives in bill-automation, so it has no
   * file-service id here; the extraction it carries is just as real, and this panel is the only place
   * a person can read it.
   */
  const hasBillToShow = (!!getFileId() || !!(billAutomationResponse !== null && billAutomationResponse !== void 0 && billAutomationResponse.billId)) && !((_property$allocations26 = property.allocations) !== null && _property$allocations26 !== void 0 && (_property$allocations26 = _property$allocations26.tempFileData) !== null && _property$allocations26 !== void 0 && _property$allocations26.isDeleted);
  /**
   * One banner per status, and the spinner only while the bill is still being polled -- the two used
   * to be decided apart, and a bill that settled anywhere but Validated or Failed spun for good (LB-5919).
   */
  const billAutomationBanner = hasBillToShow ? getBillAutomationBanner(billAutomationResponse, {
    hasErrors: hasBillAutomationErrors
  }) : BILL_AUTOMATION_BANNER.none;
  const showBillAutomationLoadingBanner = billAutomationBanner === BILL_AUTOMATION_BANNER.processing;
  const showBillAutomationSuccessBanner = billAutomationBanner === BILL_AUTOMATION_BANNER.extracted;
  /**
   * Only for a bill brought back for this cycle, and only while it is still there to act on. Saying
   * "you left this unfinished" about a file the person uploaded a moment ago would be nonsense.
   */
  const isRecoveredBill = hasBillToShow && !!((_property$allocations27 = property.allocations) !== null && _property$allocations27 !== void 0 && (_property$allocations27 = _property$allocations27.tempFileData) !== null && _property$allocations27 !== void 0 && _property$allocations27.wasRecovered) && !!(billAutomationResponse !== null && billAutomationResponse !== void 0 && billAutomationResponse.billId);
  const _useState27 = useState(''),
    _useState28 = _slicedToArray(_useState27, 2),
    dismissedBannerBillId = _useState28[0],
    setDismissedBannerBillId = _useState28[1];
  // Tied to the bill it was dismissed for, so a different one still gets to introduce itself. Today
  // the modal remounts on every cycle, property and utility change, so nothing else could reach this
  // component with a second recovered bill — but that is a key in another file, not a guarantee here.
  const showRecoveredBanner = isRecoveredBill && dismissedBannerBillId !== (billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId);
  const recoveredIntro = getRecoveredBillIntro(billAutomationResponse);
  const recoveredBillDeleteOptions = {
    allocatedSourceBillId: utility === null || utility === void 0 || (_utility$bill40 = utility.bill) === null || _utility$bill40 === void 0 ? void 0 : _utility$bill40.sourceBillId,
    isBilled: applyBill.status === 'TableReadyStatementSent',
    isClosed: (utility === null || utility === void 0 ? void 0 : utility.isEditable) === false
  };
  const mayDeleteRecoveredBill = isRecoveredBill && canDeleteRecoveredBill(billAutomationResponse, applyBill.utilityId, recoveredBillDeleteOptions);
  const isRecoveredBillAllocatedHere = isAllocatedFromBill(billAutomationResponse, recoveredBillDeleteOptions);

  /**
   * Every settled banner but the extracted one. A way out is only named when it is on screen: the
   * delete icon on an attached file, or on a recovered bill this screen uploaded for this utility. A
   * bill just uploaded to the bill bucket has neither, the same as a failed one.
   */
  const canRemoveSettledBill = !!getFileId() || mayDeleteRecoveredBill;

  /**
   * Clears the bill from this screen so another can be uploaded, for Stop and "Upload another bill".
   * Marked as a replacement first: with the screen holding no bill, the recovery in BillDetails would
   * otherwise look the cycle's bill up and put this same one straight back. Both upload paths clear the
   * mark once a new bill arrives, and closing the modal clears it with everything else.
   */
  const letGoOfBill = () => {
    var _property$allocations28, _property$allocations29;
    (_property$allocations28 = property.allocations) === null || _property$allocations28 === void 0 || _property$allocations28.setTempFileData('hasUnfinishedReplacement', true);
    (_property$allocations29 = property.allocations) === null || _property$allocations29 === void 0 || _property$allocations29.setTempFileData('billAutomationResponse', null);
  };
  /**
   * "Upload another bill" on a settled banner only lets go of the bill on screen. Not for the bill the allocation was
   * made from: saving the next one would move the allocation onto it, and an allocation only lets go of its bill by
   * that bill being deleted, which archives it with it (LB-5952).
   */
  const offersSettledUploadAnother = !canRemoveSettledBill && !isRecoveredBillAllocatedHere;
  const settledBannerCopy = getSettledBillBannerCopy(billAutomationBanner, {
    canRemoveHere: canRemoveSettledBill
  });
  const originalBillLink = getOriginalBillLink(billAutomationResponse, {
    hasBillsPage: store.account.featureFlagsStore.getIsBillsDashboardEnabled
  });
  const showBillAutomationWarningBanner = hasBillToShow && !showBillAutomationLoadingBanner && hasBillAutomationWarnings;
  const extractedDisplayValues = useMemo(() => {
    if (!extractedBillData) return {
      serviceDatesText: '',
      amountText: ''
    };
    const data = extractedBillData;
    const pickValue = keys => {
      const key = keys.find(k => data[k] !== undefined && data[k] !== null);
      return key ? data[key] : null;
    };
    const pickDateDisplay = keys => {
      const value = pickValue(keys);
      if (!value) return '-';
      const parsed = moment(value);
      return parsed.isValid() ? parsed.format('MM/DD/YY') : '-';
    };
    const startText = pickDateDisplay(['serviceStartDate', 'serviceStartDateOnly', 'startDateOnly']);
    const endText = pickDateDisplay(['serviceEndDate', 'serviceEndDateOnly', 'endDateOnly']);
    const amount = getExtractedTotalAmount(data, extractionScope);
    return {
      serviceDatesText: "".concat(startText, " - ").concat(endText),
      amountText: amount !== null ? "$ ".concat(amount) : ''
    };
  }, [extractedBillData, extractionScope]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.billDetailsWrapper
  }, /*#__PURE__*/React.createElement("form", {
    "data-tut": "reactour__allocation-form"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.serviceDate
  }, showRecoveredBanner ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.extractedBanner, " ").concat(classes.recoveredBanner),
    role: "status"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.recoveredBannerIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "info"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerTitle
  }, recoveredIntro.title), /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerText
  }, recoveredIntro.body), /*#__PURE__*/React.createElement("div", {
    className: classes.recoveredBannerNote
  }, getRecoveredBillDeleteNote(mayDeleteRecoveredBill, {
    isAllocatedHere: isRecoveredBillAllocatedHere,
    isBilled: recoveredBillDeleteOptions.isBilled,
    isClosed: recoveredBillDeleteOptions.isClosed
  })))), /*#__PURE__*/React.createElement(Button, {
    "aria-label": "Dismiss",
    className: classes.recoveredBannerDismiss,
    onClick: () => setDismissedBannerBillId((billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId) || ''),
    variant: "actionIcon"
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "close"
  }))) : null, isRecoveredBillAllocatedHere && mayDeleteRecoveredBill && !settledBannerCopy && !getFileId() ?
  /*#__PURE__*/
  // A way to another bill once this one is allocated: by deleting it, which archives the allocation made
  // from it (LB-5952), and so only for a bill this screen may delete. One it may not -- one that did not
  // come from here -- is deleted from the Bills page, which says so. The extracted banner has no
  // "Upload another bill" of its own (a settled one does, so it is not offered twice).
  // Outside the recovered banner so dismissing that does not take it away. Not while a file copy is
  // attached: its own delete is the way to another bill then, and the upload area stays hidden until
  // it is used.
  React.createElement("div", {
    className: classes.extractedBannerActions
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "text",
    className: classes.extractedBannerStopButton,
    onClick: () => {
      track(events.clicked('Upload Another Bill'), {
        page,
        status: automationStatus
      });
      uploadedBillDelete.request(billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.billId, {
        isAllocatedHere: true
      });
    },
    'custom-attribute': 'recovered-bill-upload-another'
  }, "Upload another bill")) : null, showBillAutomationLoadingBanner ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.extractedBanner, " ").concat(classes.extractedBannerLoadingOnly),
    role: "status",
    "aria-live": "polite",
    "aria-busy": "true"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.extractedBannerSpinnerWrap,
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-sparkle-animated"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerTitle
  }, "Processing your bill"), /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerText
  }, "Please wait while we extract and validate the information from your uploaded file. This may take a moment."))), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "text",
    className: classes.extractedBannerStopButton,
    onClick: () => {
      track(events.clicked('Stop Bill Extraction'), {
        page,
        status: automationStatus
      });
      letGoOfBill();
    },
    'custom-attribute': 'bill-automation-stop'
  }, t('Stop'))) : settledBannerCopy ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.extractedBanner, " ").concat(classes.extractedBannerError, " ").concat(classes.extractedBannerLoadingOnly),
    role: "alert"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.extractedBannerAiIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-error"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerErrorTitle
  }, settledBannerCopy.title), settledBannerCopy.lead ? /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerErrorIntro
  }, settledBannerCopy.lead) : null, billAutomationErrorList.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, settledBannerCopy.reasonsIntro ? /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerErrorIntro
  }, settledBannerCopy.reasonsIntro) : null, /*#__PURE__*/React.createElement("ul", {
    className: classes.extractedBannerErrorList
  }, billAutomationErrorList.map((msg, idx) => /*#__PURE__*/React.createElement("li", {
    key: idx,
    className: classes.extractedBannerErrorListItem
  }, msg)))) : settledBannerCopy.noReasons ? /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerErrorIntro
  }, settledBannerCopy.noReasons) : null, settledBannerCopy.note ? /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerErrorIntro
  }, settledBannerCopy.note) : null)), originalBillLink || offersSettledUploadAnother ? /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerActions
  }, originalBillLink ?
  /*#__PURE__*/
  // A new tab, so this modal and the allocation in progress stay where they are.
  React.createElement(Button, {
    size: "small",
    variant: "text",
    className: classes.extractedBannerStopButton,
    onClick: () => {
      track(events.clicked('View Original Bill'), {
        page,
        status: automationStatus
      });
      window.open(originalBillLink, '_blank', 'noopener,noreferrer');
    },
    'custom-attribute': 'bill-automation-view-original'
  }, "View the original bill") : null, offersSettledUploadAnother ?
  /*#__PURE__*/
  // What Stop did while the bill spun: clears it from this screen so another can be uploaded.
  // The bill itself stays on the Bills page, to be attached, retried or deleted there.
  React.createElement(Button, {
    size: "small",
    variant: "text",
    className: classes.extractedBannerStopButton,
    onClick: () => {
      track(events.clicked('Upload Another Bill'), {
        page,
        status: automationStatus
      });
      letGoOfBill();
    },
    'custom-attribute': 'bill-automation-upload-another'
  }, "Upload another bill") : null) : null) : showBillAutomationSuccessBanner ? /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBanner
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.extractedBannerSparkleIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-sparkle"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerTitle
  }, "AI extracted this bill"), /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerText
  }, "We have completed the fields below for you", additionalUtilitiesCount > 0 ? " and identified ".concat(additionalUtilitiesCount, " additional utilities across your properties in this bill.") : '.'))), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "contained",
    className: classes.extractedBannerButton,
    onClick: () => {
      track(events.clicked(showBillAutomationDetails ? 'Hide Extracted Bill Details' : 'View Extracted Bill Details'), {
        page
      });
      setShowBillAutomationDetails(v => !v);
    },
    'custom-attribute': 'bill-automation-view-details-toggle'
  }, showBillAutomationDetails ? 'Hide details' : 'View details')) : null, showBillAutomationWarningBanner ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.extractedBanner, " ").concat(classes.extractedBannerWarning, " ").concat(classes.extractedBannerLoadingOnly),
    role: "status",
    "aria-live": "polite"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.extractedBannerAiIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-warning"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerWarningTitle
  }, "Review the following warnings"), /*#__PURE__*/React.createElement("div", {
    className: classes.extractedBannerWarningIntro
  }, "We detected a few items that may need your attention:"), /*#__PURE__*/React.createElement("ul", {
    className: classes.extractedBannerWarningList
  }, billAutomationWarningList.map((msg, idx) => /*#__PURE__*/React.createElement("li", {
    key: idx,
    className: classes.extractedBannerWarningListItem
  }, msg)))))) : null, /*#__PURE__*/React.createElement(ProcessCredentialLine, {
    propertyUtilityId: applyBill.propertyUtilityId
  }), extractedBillData && showBillAutomationDetails ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BillAutomationExtractedSummary, {
    customerName: billAutomationCustomerName,
    data: scopedBillData,
    fileName: extractedBillFileName,
    scope: extractionScope,
    status: billAutomationResponse === null || billAutomationResponse === void 0 ? void 0 : billAutomationResponse.status
  }), /*#__PURE__*/React.createElement(BillAutomationExtractedLineItems, {
    data: scopedBillData
  })) : null), /*#__PURE__*/React.createElement(Grid, {
    alignItems: "flex-start",
    columnSpacing: isBillExtractionEnabled ? 12 : 2,
    container: true,
    rowSpacing: 12
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    md: billInfoAmountsRowMd,
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.sectionTitle
  }, "Bill info"), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.serviceDate).concat(!isBillExtractionEnabled ? " ".concat(classes.serviceDateInSplitRow) : '')
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    columnSpacing: 12,
    rowSpacing: 12
  }, isBillExtractionEnabled ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(FormControl, {
    variant: "outlined",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: "accountNumber"
  }, "Account number"), /*#__PURE__*/React.createElement(OutlinedInput, {
    fullWidth: true,
    id: "accountNumber",
    name: "accountNumber",
    label: "Account number"
    // Read-only rather than disabled: an account number is worth copying, and a
    // disabled input is awkward to select from. Nothing reads what is typed here.
    ,
    readOnly: true,
    value: accountNumber
  }))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(FormControl, {
    variant: "outlined",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: "invoiceNumber"
  }, "Invoice number"), /*#__PURE__*/React.createElement(OutlinedInput, {
    fullWidth: true,
    id: "invoiceNumber",
    name: "invoiceNumber",
    label: "Invoice number"
    // Locked when the extraction read one and parsed the document confidently:
    // the push uses that number and would ignore anything typed over it, so an
    // editable field here would only invite an edit that goes nowhere. Below the
    // confidence bar it opens, because then a person outranks the extractor.
    ,
    readOnly: appfolioPush.isInvoiceNumberFixed,
    disabled: isTableLocked,
    value: invoiceNumber,
    onBlur: e => {
      if (e.target.value) track(events.filled('Invoice Number'), {
        page
      });
    },
    onChange: e => setInvoiceNumber(e.target.value)
  }))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, isTableLocked ? /*#__PURE__*/React.createElement(LockedDateField, {
    id: "postingDate",
    label: "Posting date",
    value: postingDate
  }) : /*#__PURE__*/React.createElement(DatePicker, {
    format: "MM/dd/yyyy",
    label: "Posting date",
    onChange: date => {
      track(events.datePicked('Posting'), {
        page
      });
      setPostingDate(date);
    },
    value: postingDate
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.dueDatePickerWrapper
  }, isTableLocked ? /*#__PURE__*/React.createElement(LockedDateField, {
    id: "billDueDate",
    label: "Bill's due date",
    value: billDueDate
  }) : /*#__PURE__*/React.createElement(DatePicker, {
    format: "MM/dd/yyyy",
    label: "Bill's due date",
    onChange: date => {
      track(events.datePicked('Bill Due'), {
        page
      });
      setBillDueDate(date);
    },
    value: billDueDate
  }), /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: t('This due date is used when resident bill is generated.'),
    type: "grey"
  }, /*#__PURE__*/React.createElement(IconButton, {
    className: classes.dueDateInfoIcon
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "info",
    type: "question"
  })))))) : null, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.serviceDateDatePicked)
  }, /*#__PURE__*/React.createElement(RangeDatePicker, {
    disabled: isTableLocked,
    fromDate: formik.values.date.startDate ? moment(formik.values.date.startDate).toDate() : '',
    toDate: formik.values.date.endDate ? moment(formik.values.date.endDate).toDate() : '',
    format: 'MMMM yyyy',
    handleDateChange: dates => {
      track(events.datePicked('Service Dates'), {
        page
      });
      formik.setFieldTouched(formik.values.date);
      formik.handleChange({
        target: {
          id: 'date',
          value: dates
        }
      });
    },
    id: "date",
    label: "Service dates",
    maxDate: moment(store.allocations.currentCycle.endDateOnly).add(store.company.companyFeatureStore.isAppfolio ? 15 : 1, 'days').toDate(),
    reinitialize: isReinitialize,
    setIsReinitialize: setIsReinitialize,
    showPreviousMonths: isCycleFromPreviousMonth()
  })), extractedBillData ? /*#__PURE__*/React.createElement(Typography, {
    className: classes.extractedValueHint,
    variant: "body2"
  }, "Extracted: ", extractedDisplayValues.serviceDatesText) : null)))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    md: billInfoAmountsRowMd,
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.sectionTitle).concat(!isBillExtractionEnabled ? " ".concat(classes.sectionTitleAmountsSplitAlign) : '')
  }, "Amounts"), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.deductionsBox).concat(!isBillExtractionEnabled ? " ".concat(classes.deductionsBoxInSplitRow) : '')
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    columnSpacing: 12,
    rowSpacing: 12
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(FormControl, {
    onChange: e => {
      if (!/^\d+([.,]\d{0,2})?$|^$/.test(e.target.value)) return;
      formik.handleChange(e);
    },
    variant: "outlined",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: "amount"
  }, "Amount to allocate"), /*#__PURE__*/React.createElement(OutlinedInput, {
    fullWidth: true,
    disabled: isTableLocked,
    id: "amount",
    name: "amount",
    label: "Amount to allocate",
    startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "start"
    }, "$"),
    endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end"
    }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
      placement: "top",
      title: t('Amount prior to deductions. Typically excludes late fees or other line items ' + 'not passed through to residents, and may be less than the Bill total, ' + 'especially when there are multiple allocation tables for a single bill file.'),
      type: "grey"
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.amountInfoIcon
    }, /*#__PURE__*/React.createElement(Icon, {
      state: "info",
      type: "question"
    })))),
    onBlur: e => {
      if (e.target.value !== '') track(events.filled('Amount to allocate'), {
        page
      });
    },
    type: "text",
    value: formik.values.amount
  }), formik.touched.amount && !!formik.errors.amount && /*#__PURE__*/React.createElement(FormHelperText, null, translate(formik.errors.amount, t))), extractedBillData && extractedDisplayValues.amountText ? /*#__PURE__*/React.createElement(Typography, {
    className: classes.extractedValueHint,
    variant: "body2"
  }, "Extracted: ", extractedDisplayValues.amountText) : null)), isSubmeterUtility ?
  /*#__PURE__*/
  // Deliberately a plain wrapping div carrying the gap, not
  // classes.totalToAllocateAmountRow on the Grid container itself: that class's
  // marginTop overrides the negative margin-top MUI's own rowSpacing puts on a
  // Grid container (the two don't add, the later one in the cascade wins), and
  // since the FIRST item's padding-top from that same rowSpacing is untouched,
  // the override doubled the gap (12px requested rendered as 24px, measured). A
  // margin on a separate outer element sits outside that cancellation entirely
  // and does not disturb it.
  React.createElement("div", {
    style: {
      marginTop: 12
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    columnSpacing: 12,
    rowSpacing: 12
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(FormControl, {
    onChange: e => {
      if (!/^\d+([.,]\d{0,2})?$|^$/.test(e.target.value)) return;
      formik.handleChange(e);
    },
    variant: "outlined",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: "consumptionCharges"
  }, "Consumption charges"), /*#__PURE__*/React.createElement(OutlinedInput, {
    fullWidth: true,
    disabled: isTableLocked,
    id: "consumptionCharges",
    name: "consumptionCharges",
    label: "Consumption charges",
    startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "start"
    }, "$"),
    onBlur: formik.handleBlur,
    type: "text",
    value: formik.values.consumptionCharges
  }), formik.touched.consumptionCharges && !!formik.errors.consumptionCharges && /*#__PURE__*/React.createElement(FormHelperText, null, translate(formik.errors.consumptionCharges, t)))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(FormControl, {
    onChange: e => {
      if (!/^\d+([.,]\d{0,2})?$|^$/.test(e.target.value)) return;
      formik.handleChange(e);
    },
    variant: "outlined",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: "baseChargeAmount"
  }, "Base charge"), /*#__PURE__*/React.createElement(OutlinedInput, {
    fullWidth: true,
    disabled: isTableLocked,
    id: "baseChargeAmount",
    name: "baseChargeAmount",
    label: "Base charge",
    startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "start"
    }, "$"),
    onBlur: formik.handleBlur,
    type: "text",
    value: formik.values.baseChargeAmount
  }), formik.touched.baseChargeAmount && !!formik.errors.baseChargeAmount && /*#__PURE__*/React.createElement(FormHelperText, null, translate(formik.errors.baseChargeAmount, t)))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(FormControl, {
    onChange: e => {
      if (!/^\d+([.,]\d{0,4})?$|^$/.test(e.target.value)) return;
      formik.handleChange(e);
    },
    variant: "outlined",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: "totalUsage"
  }, "Bill usage"), /*#__PURE__*/React.createElement(OutlinedInput, {
    fullWidth: true,
    disabled: isTableLocked,
    id: "totalUsage",
    name: "totalUsage",
    label: "Bill usage",
    onBlur: formik.handleBlur,
    type: "text",
    value: formik.values.totalUsage
  }), formik.touched.totalUsage && !!formik.errors.totalUsage && /*#__PURE__*/React.createElement(FormHelperText, null, translate(formik.errors.totalUsage, t))))), billReconciliationWarning ? /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 8
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.allocationWarningContent
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "warning",
    state: "warning"
  }), /*#__PURE__*/React.createElement("span", null, t('Consumption charges plus Base charge does not match Amount to allocate.')))) : null) : null, isApPushEnabled ? /*#__PURE__*/React.createElement(Grid, {
    container: true,
    columnSpacing: 12,
    rowSpacing: 12,
    className: classes.totalToAllocateAmountRow
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(FormControl, {
    variant: "outlined",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: "totalToAllocateAmount"
  }, "Bill total"), /*#__PURE__*/React.createElement(OutlinedInput, {
    disabled: isTableLocked || !totalToAllocateAmount.isEditable,
    fullWidth: true,
    id: "totalToAllocateAmount",
    name: "totalToAllocateAmount",
    label: "Bill total",
    startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "start"
    }, "$"),
    endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end"
    }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
      placement: "top",
      title: t('(AP Push) The amount to be paid to the provider. This may be more than the ' + 'amount being allocated to residents, especially when the bill has more than ' + 'one utility and there are separate allocation tables for the same bill file.'),
      type: "grey"
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.amountInfoIcon
    }, /*#__PURE__*/React.createElement(Icon, {
      state: "info",
      type: "question"
    }))), totalToAllocateAmount.isEditable ? /*#__PURE__*/React.createElement(Button, {
      className: classes.totalToAllocateAmountEditButton,
      disabled: isTableLocked,
      variant: "actionButtonInvert",
      onClick: handleSaveTotalToAllocateAmount,
      'custom-attribute': '613-new-billing-module'
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "check"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, "Save")) : /*#__PURE__*/React.createElement(Button, {
      className: classes.totalToAllocateAmountEditButton,
      disabled: isTableLocked,
      variant: "actionButtonInvert",
      onClick: handleEditTotalToAllocateAmount,
      'custom-attribute': '612-new-billing-module'
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "edit"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, "Edit"))),
    onChange: e => {
      if (!/^\d+([.,]\d{0,2})?$|^$/.test(e.target.value)) return;
      setTotalToAllocateAmount(prev => _objectSpread(_objectSpread({}, prev), {}, {
        value: e.target.value,
        isEdited: true
      }));
    },
    type: "text",
    value: totalToAllocateAmountValue
  })))) : null, appfolioPush.isVisible ? /*#__PURE__*/React.createElement(AppfolioPushMessages, {
    blockers: appfolioPush.blockers,
    disabledReason: appfolioPush.disabledReason,
    liveAttempt: appfolioPush.liveAttempt,
    pushStatus: appfolioPush.pushStatus,
    refusalCode: appfolioPush.refusalCode
  }) : null, store.company.companyFeatureStore.isAppfolio && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    columnSpacing: 2,
    rowSpacing: 2
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.customChargeOnDateDatePicker)
  }, store.appfolioBillsApi.postBillSettingsAsync.inProgress || store.appfolioBillsApi.getSettingsByBillIdV2Async.inProgress || store.appfolioPropertiesApi.getDefaultChargeOnDateAsync.inProgress ? /*#__PURE__*/React.createElement(Skeleton, {
    variant: "rectangular",
    animation: "wave",
    height: 48,
    width: 220
  }) : isTableLocked ? /*#__PURE__*/React.createElement(LockedDateField, {
    id: "chargeOnDate",
    label: "Charge on date",
    value: customChargeOnDate !== null && customChargeOnDate !== void 0 ? customChargeOnDate : defaultChargeOnDateValue,
    width: 220
  }) : /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: 'top',
    title: customChargeOnDate ? '' : datePickerLabel,
    type: 'success-bold-dark'
  }, /*#__PURE__*/React.createElement(DatePicker, {
    format: "MM/dd/yyyy",
    label: getLabel(),
    onChange: date => {
      const billId = id;
      const formatedDate = date === null || date === '' ? null : moment(date).format('YYYY-MM-DD');
      track(events.datePicked('Charge On'), {
        page
      });
      setCustomChargeOnDate(formatedDate);
      if (!!billId && billId !== '') updateBillChargeOnDate(billId, formatedDate);
    },
    value: customChargeOnDate !== null ? moment(customChargeOnDate).toDate() : null
  })), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    style: {
      width: '48px',
      height: '48px',
      marginLeft: '6px'
    },
    disabled: isTableLocked || customChargeOnDate === null || customChargeOnDate === undefined,
    onClick: () => {
      const billId = id;
      track(events.clicked('Clear Charge On Date'), {
        page
      });
      setCustomChargeOnDate(null);
      if (!!billId && billId !== '') {
        updateBillChargeOnDate(billId, null);
      }
    },
    'custom-attribute': '858-property-utilities-setup-page'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "close"
  })), /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: t("Set the date to post the charge or credit — if you leave it blank, we’ll use date from property settings. If it's not set - we’ll use today’s date."),
    type: "grey"
  }, /*#__PURE__*/React.createElement(IconButton, null, /*#__PURE__*/React.createElement(Icon, {
    state: "info",
    type: "question"
  }))))))))), /*#__PURE__*/React.createElement("div", {
    className: classes.sectionTitle
  }, "Deductions"), /*#__PURE__*/React.createElement("div", {
    className: classes.deductionsBox
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.deductionItem
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.deductionItemData
  }, "Common Area Deduction(CAD): ", /*#__PURE__*/React.createElement("b", null, billInitialValues.commonAreaDeduction, "%"), isSubmeterUtility && /*#__PURE__*/React.createElement("div", {
    className: classes.deductionLockedNote
  }, t('Not applied to metered billing.'))), /*#__PURE__*/React.createElement("div", {
    className: classes.deductionItemEdit
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    disabled: true,
    'custom-attribute': '606-new-billing-module'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "lock-locked"
  }), /*#__PURE__*/React.createElement("span", {
    className: "actionText"
  }, "Edit")))), /*#__PURE__*/React.createElement("div", {
    className: classes.deductionItem
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.deductionItemData
  }, "Good Will Deduction (GWD): ", /*#__PURE__*/React.createElement("b", null, billInitialValues.goodWillDeduction, "%"), isSubmeterUtility && /*#__PURE__*/React.createElement("div", {
    className: classes.deductionLockedNote
  }, t('Not applied to metered billing.'))), /*#__PURE__*/React.createElement("div", {
    className: classes.deductionItemEdit
  }, isSubmeterUtility ? /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    disabled: true,
    'custom-attribute': '607-new-billing-module'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "lock-locked"
  }), /*#__PURE__*/React.createElement("span", {
    className: "actionText"
  }, "Edit")) : /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => {
      track(events.clicked('Edit GWD'), {
        page,
        utility: applyBill.utilityName
      });
      setIsGwdSetting(true);
    },
    'custom-attribute': '607-new-billing-module'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "edit"
  }), /*#__PURE__*/React.createElement("span", {
    className: "actionText"
  }, "Edit"))), !isSubmeterUtility && isGwdSetting ? /*#__PURE__*/React.createElement(Gwd, {
    cadValue: billInitialValues.commonAreaDeduction,
    formik: formik,
    initialGWD: billInitialValues.goodWillDeduction,
    handleClose: () => setIsGwdSetting(false),
    propertyId: propertyId
  }) : null))), getComparisonBlock()), /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.modalFooterStandartSmall
  }, /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.footerEven
  }, /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.footerWrapper
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => {
      track(events.clicked('Close'), {
        page,
        utility: applyBill.utilityName
      });
      handleClose();
    },
    variant: "text",
    'custom-attribute': '608-new-billing-module'
  }, t('Close'))), /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.footerWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: modalFooterClasses.footerButton
  }, appfolioPush.isVisible ? /*#__PURE__*/React.createElement(Button, {
    className: classes.pushApButton,
    disabled: !appfolioPush.canPush || totalToAllocateAmount.isEditable,
    onClick: handlePushApToAppFolio,
    variant: "text",
    'custom-attribute': '611-new-billing-module'
  }, t('Push AP to AppFolio'), /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: t('Push this bill to AppFolio without calculating an allocation.'),
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.pushApInfoIcon,
    onClick: e => e.stopPropagation()
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "info",
    type: "info"
  })))) : null, /*#__PURE__*/React.createElement(Button, {
    disabled: isTableLocked,
    onClick: handleSkipBill,
    variant: "text",
    'custom-attribute': '610-new-billing-module'
  }, "Skip bill"), /*#__PURE__*/React.createElement(Button, {
    disabled: isTableLocked,
    onClick: formik.handleSubmit,
    type: "submit",
    variant: "contained",
    'custom-attribute': '609-new-billing-module'
  }, "Calculate allocation"))))), /*#__PURE__*/React.createElement(SimpleModal, {
    handleClose: handleCancelRecalculate,
    isOpen: pendingRecalculateConfirm,
    noPadding: true,
    rightButton: /*#__PURE__*/React.createElement(Button, {
      onClick: handleConfirmRecalculate,
      type: "button",
      variant: "contained"
    }, t('Save and recalculate')),
    title: t('Recalculate meter amounts?')
  }, /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    icon: false
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, t('Saving this bill will re-price the amounts on the Meter details screen from the new bill values. ' + 'Rates you typed are kept. If a unit is no longer mapped on this utility, its charges are removed.')))), uploadedBillDelete.confirmation, oldServicePeriod.confirmation);
});
export default BillDetailsForm;